import {
  Add as AddIcon,
  CloseOutlined as CancelIcon,
  ContactPhone,
  OutlinedFlag as CountryIcon,
  Delete as DeleteIcon,
  EditOutlined as EditIcon,
  LocationOnOutlined as LocationIcon,
  MailOutline as MailIcon,
  AttachMoney as MoneyIcon,
  PersonOutline as PersonIcon,
  PhoneEnabledOutlined as PhoneIcon,
  CheckOutlined as SaveIcon,
  Email as EmailManageIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { geocodeByAddress } from "react-places-autocomplete";
import { BaseModalComponent } from "src/components/common/BaseModalComponent";
import { useModals } from "src/context/ModalContext";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import countriesData from "../../../../data/countriesList.json";
import { AddressSearchField } from "./AddressSearchFIeld";
import { useUpdateWorkforce } from "./hooks/useWorkForce";
import { WorkforceDetailTabs } from "../../components/WorkforceDetailTabs";

export const SUPER_LIGHT_BORDER = "#f1f3f9";

const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = ("" + phone).replace(/\D/g, "");
  const match = cleaned.match(/^(\d*)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? `+${match[1]} ` : "+1 ";
    return `${intlCode}(${match[2]}) ${match[3]}—${match[4]}`;
  }
  return phone;
};

const InfoCell = memo(function InfoCell({ icon: Icon, label, name, value, isEditing, onChange, children, rightAction, isLastRow = false }) {
  return (
    <Box
      sx={{
        p: "6px 24px",
        display: "flex",
        alignItems: "center",
        gap: 2,
        minHeight: 60,
        borderBottom: isLastRow ? "none" : `1px solid ${SUPER_LIGHT_BORDER}`,
        overflow: isEditing ? "visible" : "hidden",
        position: "relative",
      }}
    >
      {/* Tu Icon Box Naranja */}
      <Box
        sx={{
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "10px",
          backgroundColor: "var(--fx-colors-warning-50, #FFF7ED)",
          color: "#f97316",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "var(--fx-surface-texttertiary, #94A3B8)",
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.68rem",
              mb: 0.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </Typography>

          {isEditing ? (
            (children ?? (
              <TextField
                fullWidth
                name={name}
                value={value ?? ""}
                onChange={onChange}
                variant="standard"
                size="small"
                autoComplete="off"
                InputProps={{ sx: { fontSize: "0.8rem" } }}
              />
            ))
          ) : (
            <Tooltip
              title={value || ""}
              arrow
              placement="top-start"
              PopperProps={{
                sx: {
                  zIndex: 9999999,
                  pointerEvents: "none",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "var(--fx-surface-text, #0F172A)",
                  fontSize: "0.8rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  cursor: "default",
                }}
              >
                {value || "—"}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {rightAction}
      </Box>
    </Box>
  );
});

export function PersonalInformation({ workforce }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(workforce);
  const seePhonesTriggerRef = useRef(null);
  const seeEmailsTriggerRef = useRef(null);
  const seeEditModalTriggerRef = useRef(null);
  const { closeByKey } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const { mutate, isPending } = useUpdateWorkforce();

  useEffect(() => {
    setFormData(workforce);
  }, [workforce]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectAddress = async (address) => {
    try {
      const results = await geocodeByAddress(address);
      const selectedPlace = results[0];
      const components = selectedPlace.address_components;

      const getComponent = (types) => {
        const component = components.find((c) => types.some((type) => c.types.includes(type)));
        return component ? component.long_name : "";
      };

      const placeName = getComponent(["premise", "point_of_interest", "establishment"]);
      const fullAddress = selectedPlace.formatted_address;

      setFormData((prev) => ({
        ...prev,
        address: placeName ? `${placeName}, ${fullAddress}` : fullAddress,
        city: getComponent(["locality", "sublocality", "postal_town"]),
        state: getComponent(["administrative_area_level_1"]),
        country: getComponent(["country"]),
        zipCode: getComponent(["postal_code"]),
      }));
    } catch (error) {
      console.error("Error seleccionando la ubicación:", error);
    }
  };

  const handleSave = () => {
    mutate({ uid: workforce.uid, data: formData }, { onSuccess: () => setIsEditing(false) });
  };

  const handleCancel = () => {
    setFormData(workforce);
    setIsEditing(false);
  };

  // ── Phone helpers ────────────────────────────────────────────────────────
  const getPrimaryPhone = (phoneNumbers, fallbackPhone) => {
    if (!Array.isArray(phoneNumbers) || phoneNumbers.length === 0) {
      return fallbackPhone || "";
    }
    const primary = phoneNumbers.find((p) => p.isDefault);
    return primary ? primary.phone : phoneNumbers[0].phone;
  };

  const handleSavePhones = (updatedPhones) => {
    const primaryPhone = getPrimaryPhone(updatedPhones, "");
    const updatedData = {
      ...formData,
      phoneNumbers: updatedPhones,
      phone: primaryPhone,
    };

    if (isEditing) {
      setFormData(updatedData);
      closeByKey("Manage-Phones-Modal");
    } else {
      mutate(
        { uid: workforce.uid, data: updatedData },
        {
          onSuccess: () => {
            setFormData(updatedData);
            closeByKey("Manage-Phones-Modal");
            showSnackbar("Phone numbers updated successfully", "success");
          },
          onError: (err) => {
            const errMsg = err?.response?.data?.message || err?.message || "Failed to update phone numbers";
            showSnackbar(errMsg, "error");
          },
        },
      );
    }
  };

  const displayPhone = getPrimaryPhone(formData.phoneNumbers, formData.phone);
  const phoneCount = Array.isArray(formData.phoneNumbers) ? formData.phoneNumbers.length : 0;

  // ── Email helpers ────────────────────────────────────────────────────────
  const getPrimaryEmail = (emails, fallbackEmail) => {
    if (!Array.isArray(emails) || emails.length === 0) return fallbackEmail || "";
    const primary = emails.find((e) => e.isDefault);
    return primary ? primary.email : emails[0].email;
  };

  const handleSaveEmails = (updatedEmails) => {
    const primaryEmail = getPrimaryEmail(updatedEmails, "");
    const updatedData = {
      ...formData,
      emails: updatedEmails,
      email: primaryEmail,
    };

    if (isEditing) {
      setFormData(updatedData);
      closeByKey("Manage-Emails-Modal");
    } else {
      mutate(
        { uid: workforce.uid, data: updatedData },
        {
          onSuccess: () => {
            setFormData(updatedData);
            closeByKey("Manage-Emails-Modal");
            showSnackbar("Email addresses updated successfully", "success");
          },
          onError: (err) => {
            const errMsg = err?.response?.data?.message || err?.message || "Failed to update email addresses";
            showSnackbar(errMsg, "error");
          },
        },
      );
    }
  };

  const displayEmail = getPrimaryEmail(formData.emails, formData.email);
  const emailCount = Array.isArray(formData.emails) ? formData.emails.length : 0;

  // ── Right-action buttons ────────────────────────────────────────────────
  const phoneRightAction = (
    <Tooltip title={isEditing ? "Manage phone numbers" : "Manage phone numbers"} arrow>
      <IconButton size="small" onClick={() => seePhonesTriggerRef.current?.click()} sx={{ color: "#f97316" }}>
        <ContactPhone sx={{ fontSize: 18 }} />
        {phoneCount > 1 && (
          <Typography component="span" variant="caption" sx={{ fontSize: "0.6rem", ml: 0.25, fontWeight: 700 }}>
            {phoneCount}
          </Typography>
        )}
      </IconButton>
    </Tooltip>
  );

  const emailRightAction = (
    <Tooltip title="Manage email addresses" arrow>
      <IconButton size="small" onClick={() => seeEmailsTriggerRef.current?.click()} sx={{ color: "#f97316" }}>
        <EmailManageIcon sx={{ fontSize: 18 }} />
        {emailCount > 1 && (
          <Typography component="span" variant="caption" sx={{ fontSize: "0.6rem", ml: 0.25, fontWeight: 700 }}>
            {emailCount}
          </Typography>
        )}
      </IconButton>
    </Tooltip>
  );

  // ── Coverage Area helpers (Location & Country) ───────────────────────────
  const coverageArea =
    (Array.isArray(formData?.coverageAreas) && formData.coverageAreas.length > 0
      ? formData.coverageAreas.find((c) => c?.isDefault) || formData.coverageAreas[0]
      : null) ||
    (Array.isArray(workforce?.coverageAreas) && workforce.coverageAreas.length > 0
      ? workforce.coverageAreas.find((c) => c?.isDefault) || workforce.coverageAreas[0]
      : null);

  const displayLocation = coverageArea
    ? coverageArea.address || (coverageArea.city ? `${coverageArea.city}${coverageArea.state ? ", " + coverageArea.state : ""}` : "—")
    : formData.address
    ? formData.city
      ? `${formData.address} - ${formData.city}`
      : formData.address
    : "—";

  const displayCountry = coverageArea?.country || formData.country || "—";

  return (
    <Box sx={{ borderRadius: "12px", border: `1px solid ${SUPER_LIGHT_BORDER}`, position: "relative" }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600, ml: 2, mt: 2 }}>Personal information</Typography>

      <Box sx={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 1 }}>
        <IconButton onClick={() => seeEditModalTriggerRef.current?.click()} size="small">
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container>
        <Grid item xs={12} sm={6}>
          <InfoCell
            icon={PersonIcon}
            label="Full Name"
            name="alias"
            value={formData.firstName + " " + formData.lastName}
            isEditing={isEditing}
            onChange={handleChange}
          />
        </Grid>

        {/* Email — always read-only; managed via dialog */}
        <Grid item xs={12} sm={6}>
          <InfoCell
            icon={MailIcon}
            label="Email"
            value={displayEmail}
            isEditing={false}
            rightAction={emailRightAction}
          />
        </Grid>

        {/* Phone — always read-only; managed via dialog */}
        <Grid item xs={12} sm={6}>
          <InfoCell
            icon={PhoneIcon}
            label="Phone"
            value={formatPhoneNumber(displayPhone)}
            isEditing={false}
            rightAction={phoneRightAction}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <InfoCell
            icon={LocationIcon}
            label="Location"
            value={displayLocation}
            isEditing={isEditing}
          >
            <AddressSearchField
              value={formData.address || ""}
              onChange={(v) => setFormData((p) => ({ ...p, address: v }))}
              onSelect={handleSelectAddress}
              containerStyle={{ zIndex: 10000, position: "relative" }}
            />
          </InfoCell>
        </Grid>

        <Grid item xs={12} sm={6}>
          <InfoCell
            icon={MoneyIcon}
            label="Hourly Rate"
            name="ratePerHour"
            value={isEditing ? formData.ratePerHour : `$ ${formData.ratePerHour}`}
            isEditing={isEditing}
            onChange={handleChange}
            isLastRow
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <InfoCell icon={CountryIcon} label="Country" value={displayCountry} isEditing={isEditing} isLastRow />
        </Grid>
      </Grid>

      {/* Hidden trigger for Phones modal */}
      <BaseModalComponent
        modalLabel="Manage Phone Numbers"
        modalKey="Manage-Phones-Modal"
        defaultSizes={{ width: 600, height: 400 }}
        elementTrigger={<button ref={seePhonesTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
      >
        <ManagePhonesDialogContent
          phoneNumbers={formData.phoneNumbers || []}
          onSave={handleSavePhones}
          onClose={() => closeByKey("Manage-Phones-Modal")}
          isSaving={!isEditing && isPending}
        />
      </BaseModalComponent>

      {/* Hidden trigger for Emails modal */}
      <BaseModalComponent
        modalLabel="Manage Email Addresses"
        modalKey="Manage-Emails-Modal"
        defaultSizes={{ width: 600, height: 400 }}
        elementTrigger={<button ref={seeEmailsTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
      >
        <ManageEmailsDialogContent
          emails={formData.emails || []}
          onSave={handleSaveEmails}
          onClose={() => closeByKey("Manage-Emails-Modal")}
          isSaving={!isEditing && isPending}
        />
      </BaseModalComponent>

      {/* Hidden trigger for Edit Workforce Modal (WorkforceDetailTabs) */}
      <BaseModalComponent
        modalLabel="Edit Workforce"
        modalKey="Edit-Workforce-Modal"
        defaultSizes={{ width: 900, height: 600 }}
        elementTrigger={<button ref={seeEditModalTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
      >
        <WorkforceDetailTabs
          workforce={workforce}
          onUpdated={() => {
            closeByKey("Edit-Workforce-Modal");
          }}
        />
      </BaseModalComponent>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ManagePhonesDialogContent
// ─────────────────────────────────────────────────────────────────────────────
function ManagePhonesDialogContent({ phoneNumbers, onSave, onClose, isSaving }) {
  const [phones, setPhones] = useState([]);

  useEffect(() => {
    const initialPhones =
      Array.isArray(phoneNumbers) && phoneNumbers.length > 0
        ? phoneNumbers.map((p) => ({
            countryCode: p.countryCode || "US",
            dialCode: p.dialCode || "+1",
            phone: p.phone || "",
            tag: p.tag || "other",
            isDefault: !!p.isDefault,
          }))
        : [{ countryCode: "US", dialCode: "+1", phone: "", tag: "other", isDefault: true }];

    const hasDefault = initialPhones.some((p) => p.isDefault);
    if (!hasDefault && initialPhones.length > 0) {
      initialPhones[0].isDefault = true;
    }

    setPhones(initialPhones);
  }, [phoneNumbers]);

  const handleAdd = () => {
    setPhones((prev) => [...prev, { countryCode: "US", dialCode: "+1", phone: "", tag: "other", isDefault: prev.length === 0 }]);
  };

  const handleRemove = (index) => {
    if (phones.length <= 1) return;
    setPhones((prev) => {
      const updated = prev.filter((_, idx) => idx !== index);
      const wasDefault = prev[index].isDefault;
      if (wasDefault && updated.length > 0) {
        updated[0].isDefault = true;
      }
      return updated;
    });
  };

  const handleChange = (index, field, value) => {
    setPhones((prev) => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)));
  };

  const handleCountryChange = (index, comboValue) => {
    const [code, dial] = comboValue.split("-");
    setPhones((prev) => prev.map((item, idx) => (idx === index ? { ...item, countryCode: code, dialCode: dial } : item)));
  };

  const handleSetDefault = (index) => {
    setPhones((prev) => prev.map((item, idx) => ({ ...item, isDefault: idx === index })));
  };

  const handleSave = () => {
    const validPhones = phones
      .map((p) => ({
        ...p,
        phone: p.phone.trim().replace(/[^\d+]/g, ""),
      }))
      .filter((p) => p.phone);

    if (validPhones.length === 0) {
      alert("At least one valid phone number is required.");
      return;
    }

    const hasDefault = validPhones.some((p) => p.isDefault);
    if (!hasDefault && validPhones.length > 0) {
      validPhones[0].isDefault = true;
    }

    onSave(validPhones);
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, maxHeight: 400, overflowY: "auto", pr: 1 }}>
        {phones.map((phoneItem, index) => {
          const countryVal = `${phoneItem.countryCode}-${phoneItem.dialCode}`;
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                p: 2,
                borderRadius: "10px",
                border: "1px solid var(--fx-surface-border, #E2E8F0)",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <FormControl size="small" sx={{ width: 110 }}>
                <InputLabel>Country</InputLabel>
                <Select
                  value={countryVal}
                  label="Country"
                  onChange={(e) => handleCountryChange(index, e.target.value)}
                  renderValue={(selected) => {
                    const [code, dial] = selected.split("-");
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ReactCountryFlag countryCode={code} svg style={{ width: 18, height: 18 }} />
                        <span>{dial}</span>
                      </Box>
                    );
                  }}
                >
                  {countriesData.map((c) => (
                    <MenuItem key={c.code} value={`${c.code}-${c.dialCode}`}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <ReactCountryFlag countryCode={c.code} svg style={{ width: 20, height: 20 }} />
                        <span>
                          {c.name} ({c.dialCode})
                        </span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Phone Number"
                value={phoneItem.phone}
                onChange={(e) => handleChange(index, "phone", e.target.value)}
                sx={{ flex: 1, minWidth: 150 }}
                variant="outlined"
                placeholder="Phone number"
              />

              <FormControl size="small" sx={{ width: 120 }}>
                <InputLabel>Label</InputLabel>
                <Select value={phoneItem.tag || "other"} label="Label" onChange={(e) => handleChange(index, "tag", e.target.value)}>
                  <MenuItem value="mobile">Mobile</MenuItem>
                  <MenuItem value="work">Work</MenuItem>
                  <MenuItem value="home">Home</MenuItem>
                  <MenuItem value="secondary">Secondary</MenuItem>
                  <MenuItem value="primary">Primary</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={<Radio checked={!!phoneItem.isDefault} onChange={() => handleSetDefault(index)} color="warning" size="small" />}
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                    Default
                  </Typography>
                }
                sx={{ mr: 0 }}
              />

              {phones.length > 1 && (
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleRemove(index)}
                  sx={{
                    p: 1,
                    border: "1px solid",
                    borderColor: "error.light",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "error.light", color: "#fff" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          );
        })}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAdd}
          variant="text"
          color="warning"
          sx={{ alignSelf: "flex-start", fontWeight: 600, textTransform: "none" }}
        >
          Add Phone Number
        </Button>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: "1px solid var(--fx-surface-border, #E2E8F0)", pt: 2 }}>
        <Button onClick={onClose} disabled={isSaving} sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          variant="contained"
          color="warning"
          sx={{
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            bgcolor: "#f97316",
            "&:hover": { bgcolor: "#ea580c", boxShadow: "none" },
          }}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ManageEmailsDialogContent
// ─────────────────────────────────────────────────────────────────────────────
function ManageEmailsDialogContent({ emails, onSave, onClose, isSaving }) {
  const [emailList, setEmailList] = useState([]);

  useEffect(() => {
    const initial =
      Array.isArray(emails) && emails.length > 0
        ? emails.map((e) => ({
            email: e.email || "",
            tag: e.tag || "work",
            isDefault: !!e.isDefault,
          }))
        : [{ email: "", tag: "work", isDefault: true }];

    const hasDefault = initial.some((e) => e.isDefault);
    if (!hasDefault && initial.length > 0) {
      initial[0].isDefault = true;
    }

    setEmailList(initial);
  }, [emails]);

  const handleAdd = () => {
    setEmailList((prev) => [...prev, { email: "", tag: "work", isDefault: prev.length === 0 }]);
  };

  const handleRemove = (index) => {
    if (emailList.length <= 1) return;
    setEmailList((prev) => {
      const updated = prev.filter((_, idx) => idx !== index);
      const wasDefault = prev[index].isDefault;
      if (wasDefault && updated.length > 0) {
        updated[0].isDefault = true;
      }
      return updated;
    });
  };

  const handleChange = (index, field, value) => {
    setEmailList((prev) => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)));
  };

  const handleSetDefault = (index) => {
    setEmailList((prev) => prev.map((item, idx) => ({ ...item, isDefault: idx === index })));
  };

  const handleSave = () => {
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = emailList.filter((e) => EMAIL_REGEX.test(e.email.trim()));

    if (validEmails.length === 0) {
      alert("At least one valid email address is required.");
      return;
    }

    const hasDefault = validEmails.some((e) => e.isDefault);
    if (!hasDefault && validEmails.length > 0) {
      validEmails[0].isDefault = true;
    }

    onSave(validEmails.map((e) => ({ ...e, email: e.email.trim() })));
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, maxHeight: 400, overflowY: "auto", pr: 1 }}>
        {emailList.map((emailItem, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
              p: 2,
              borderRadius: "10px",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <TextField
              size="small"
              label="Email Address"
              type="email"
              value={emailItem.email}
              onChange={(e) => handleChange(index, "email", e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              variant="outlined"
              placeholder="email@example.com"
            />

            <FormControl size="small" sx={{ width: 130 }}>
              <InputLabel>Label</InputLabel>
              <Select value={emailItem.tag || "work"} label="Label" onChange={(e) => handleChange(index, "tag", e.target.value)}>
                <MenuItem value="work">Work</MenuItem>
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="home">Home</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Radio checked={!!emailItem.isDefault} onChange={() => handleSetDefault(index)} color="warning" size="small" />}
              label={
                <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                  Default
                </Typography>
              }
              sx={{ mr: 0 }}
            />

            {emailList.length > 1 && (
              <IconButton
                color="error"
                size="small"
                onClick={() => handleRemove(index)}
                sx={{
                  p: 1,
                  border: "1px solid",
                  borderColor: "error.light",
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "error.light", color: "#fff" },
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAdd}
          variant="text"
          color="warning"
          sx={{ alignSelf: "flex-start", fontWeight: 600, textTransform: "none" }}
        >
          Add Email Address
        </Button>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: "1px solid var(--fx-surface-border, #E2E8F0)", pt: 2 }}>
        <Button onClick={onClose} disabled={isSaving} sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          variant="contained"
          color="warning"
          sx={{
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            bgcolor: "#f97316",
            "&:hover": { bgcolor: "#ea580c", boxShadow: "none" },
          }}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
